import { useState } from 'react';
import { useGT } from 'gt-react';
import { usePreviewPayment } from '@/api/rpc/hooks/finance/usePayments';
import { Skeleton } from '@/components/ui/skeleton';
import type { Payment } from '@erp/rpc';

type InlineImageKind = 'preview' | 'check-image';

/**
 * State + handlers for the inline payment image (provider preview or check
 * image). Owns the fetch/visibility lifecycle so PaymentDetails stays lean.
 */
export function usePaymentInlineImage(payment: Payment) {
  const gt = useGT();
  const previewPayment = usePreviewPayment();
  const externalPayment = payment.externalPayment;

  const [inlineImageKind, setInlineImageKind] =
    useState<InlineImageKind | null>(null);
  const [imageViewerSrc, setImageViewerSrc] = useState<string | null>(null);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageViewerError, setImageViewerError] = useState<string | null>(null);

  const canPreviewImage = externalPayment?.allowedActions.canPreview ?? false;
  const canViewCheckImage = Boolean(externalPayment?.imageUri);
  const isPreviewVisible = inlineImageKind === 'preview';
  const isCheckImageVisible = inlineImageKind === 'check-image';
  const imageViewerTitle =
    inlineImageKind === 'check-image'
      ? gt('Check Image')
      : gt('Payment Preview');
  const imageButtonLabel = canPreviewImage
    ? isPreviewVisible
      ? gt('Hide Preview')
      : gt('Preview')
    : isCheckImageVisible
      ? gt('Hide Check Image')
      : gt('View Check Image');

  function resetInlineImage() {
    setInlineImageKind(null);
    setImageViewerSrc(null);
    setImageViewerError(null);
    setIsImageLoading(false);
  }

  function handlePreviewClick() {
    if (isPreviewVisible) {
      resetInlineImage();
      return;
    }

    setInlineImageKind('preview');
    setImageViewerSrc(null);
    setImageViewerError(null);
    setIsImageLoading(true);
    previewPayment.mutate(
      { id: payment.id },
      {
        onSuccess: (data) => {
          setInlineImageKind('preview');
          setImageViewerSrc(
            `data:${data.imageMimeType};base64,${data.imageBase64}`,
          );
          setImageViewerError(null);
          setIsImageLoading(false);
        },
        onError: (error) => {
          setInlineImageKind('preview');
          setImageViewerSrc(null);
          setImageViewerError(error.message || gt('Unable to load preview.'));
          setIsImageLoading(false);
        },
      },
    );
  }

  function handleViewCheckImageClick() {
    if (!externalPayment?.imageUri) return;
    if (isCheckImageVisible) {
      resetInlineImage();
      return;
    }

    setInlineImageKind('check-image');
    setImageViewerSrc(externalPayment.imageUri);
    setImageViewerError(null);
    setIsImageLoading(true);
  }

  return {
    canPreviewImage,
    canViewCheckImage,
    isPreviewLoading: previewPayment.isPending && inlineImageKind === 'preview',
    isPreviewPending: previewPayment.isPending,
    imageButtonLabel,
    handleImageButtonClick: canPreviewImage
      ? handlePreviewClick
      : handleViewCheckImageClick,
    viewer: {
      title: imageViewerTitle,
      src: imageViewerSrc,
      isLoading: isImageLoading,
      error: imageViewerError,
      onLoaded: () => setIsImageLoading(false),
      onLoadError: () => {
        setIsImageLoading(false);
        setImageViewerError(gt('Unable to load image.'));
      },
    },
  };
}

interface PaymentImageViewerPanelProps {
  paymentId: string;
  viewer: ReturnType<typeof usePaymentInlineImage>['viewer'];
}

/** Inline panel rendering the fetched payment preview / check image. */
export function PaymentImageViewerPanel({
  paymentId,
  viewer,
}: PaymentImageViewerPanelProps) {
  if (!viewer.src && !viewer.isLoading && !viewer.error) return null;

  return (
    <div className="space-y-2">
      <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {viewer.title}
      </h4>

      {viewer.isLoading && <Skeleton className="h-[32rem] w-full rounded-md" />}

      {viewer.src && (
        <img
          src={viewer.src}
          alt={`${viewer.title} for payment ${paymentId}`}
          className={`w-full rounded-md border ${
            viewer.isLoading ? 'hidden' : 'block'
          }`}
          onLoad={viewer.onLoaded}
          onError={viewer.onLoadError}
        />
      )}

      {viewer.error && (
        <div className="text-sm text-destructive">{viewer.error}</div>
      )}
    </div>
  );
}
